/**
 * PixelIcon — tiny code-native pixel glyphs for stat, cost and status markers
 * (same technique as CardGlyph / SeatGlyph: no external image assets, no runtime
 * drawing). Each icon is a bitmap of '.' (empty) and '1' (filled) cells rendered
 * as <rect>s. Icons paint in `currentColor`, so they inherit the surrounding text
 * colour — the existing `data-tone` and health/signal colouring keeps working
 * untouched — and size to `1em` via CSS, so they slot in wherever a text glyph
 * used to sit.
 */
import { memo } from 'react';
import type { ReactElement } from 'react';

export type PixelIconName =
  // Card stats & costs
  | 'attack'
  | 'health'
  | 'energy'
  | 'deck'
  | 'signal'
  // Board & effect statuses
  | 'effect'
  | 'rush'
  | 'heat'
  | 'freeze'
  | 'shield'
  | 'delay'
  | 'prohibited'
  | 'hack'
  | 'controller'
  | 'capture'
  | 'attachment'
  | 'buff'
  | 'debuff'
  | 'timedDestroy'
  | 'damaged'
  | 'droplet'
  | 'check'
  | 'lock'
  | 'gear'
  | 'redZone'
  | 'bounce'
  | 'copy'
  | 'trash'
  // Plant & sun statuses
  | 'thorns'
  | 'growth'
  | 'sunflower'
  | 'bloom'
  | 'sun'
  // Card statuses and marks added with the card faces (design system 01 §9).
  | 'fullLocation'
  | 'barrier'
  | 'spotlight'
  | 'support'
  | 'soften'
  | 'net'
  | 'clot'
  | 'token'
  | 'statUp'
  | 'statDown'
  | 'crack'
  // Location markers
  | 'location0'
  | 'location1'
  | 'location2'
  // Form controls (design system 02 §2.6): rich-text toolbar and select arrow.
  | 'bold'
  | 'italic'
  | 'bulletList'
  | 'chevronDown'
  | 'search'
  // Deck actions (05 §4.1).
  | 'paste'
  | 'archive'
  | 'plus'
  | 'minus'
  // Top-menu navigation. These are drawn on a 9x9 grid instead of the 7x7 used
  // by the inline glyphs: the menu renders them in a fixed 18px box (exactly
  // 2 device pixels per cell), which affords the extra detail. Inline glyphs
  // size to `1em` of text as small as 6.5px, where 9x9 would drop rows.
  | 'home'
  | 'decks'
  | 'catalog'
  | 'challenges'
  | 'sandbox'
  | 'feedback'
  | 'changelog'
  | 'logout';

// ◈ — outline diamond with a centre pip (signal / generic active effect).
const DIAMOND_SIGNAL = [
  '...1...',
  '..1.1..',
  '.1...1.',
  '1..1..1',
  '.1...1.',
  '..1.1..',
  '...1...',
] as const;

// ◆ — solid diamond (foreign controller / temporary capture).
const DIAMOND_SOLID = [
  '...1...',
  '..111..',
  '.11111.',
  '1111111',
  '.11111.',
  '..111..',
  '...1...',
] as const;

const ICONS: Record<PixelIconName, readonly string[]> = {
  // ⚔ — upright sword.
  attack: [
    '...1...',
    '..111..',
    '..111..',
    '..111..',
    '.11111.',
    '...1...',
    '...1...',
  ],
  // ♥ — heart.
  health: [
    '.11.11.',
    '1111111',
    '1111111',
    '1111111',
    '.11111.',
    '..111..',
    '...1...',
  ],
  // ⚡ — lightning bolt.
  energy: [
    '....1..',
    '...11..',
    '..11...',
    '.11111.',
    '...11..',
    '..11...',
    '.11....',
  ],
  // 🂠 — two staggered solid cards, the 7x7 inline cut of the menu's `decks`.
  deck: [
    '....111',
    '....111',
    '111.111',
    '111.111',
    '111.111',
    '111....',
    '111....',
  ],
  signal: DIAMOND_SIGNAL,
  effect: DIAMOND_SIGNAL,
  // ↯ — double chevron (Rush: may strike on reveal).
  rush: [
    '1...1..',
    '1...1..',
    '11..11.',
    '111.111',
    '11..11.',
    '1...1..',
    '1...1..',
  ],
  // Flame — Heat that reaches the common meltdown threshold.
  heat: [
    '...1...',
    '..11...',
    '.1111..',
    '.111...',
    '11111..',
    '.11111.',
    '..111..',
  ],
  // Snowflake — Freeze skips upcoming combats.
  freeze: [
    '...1...',
    '1..1..1',
    '.1.1.1.',
    '..111..',
    '.1.1.1.',
    '1..1..1',
    '...1...',
  ],
  // ⬙ — shield.
  shield: [
    '1111111',
    '1111111',
    '1111111',
    '.11111.',
    '.11111.',
    '..111..',
    '...1...',
  ],
  // ◌ — hollow ring (attack delay).
  delay: [
    '..111..',
    '.1...1.',
    '1.....1',
    '1.....1',
    '1.....1',
    '.1...1.',
    '..111..',
  ],
  // × — cannot attack.
  prohibited: [
    '1.....1',
    '.1...1.',
    '..1.1..',
    '...1...',
    '..1.1..',
    '.1...1.',
    '1.....1',
  ],
  // ⌁ — glitch "Z" (Hack).
  hack: [
    '1111111',
    '....11.',
    '...11..',
    '..11...',
    '.11....',
    '11.....',
    '1111111',
  ],
  controller: DIAMOND_SOLID,
  capture: DIAMOND_SOLID,
  // ◇ — hollow diamond (attached virus).
  attachment: [
    '...1...',
    '..1.1..',
    '.1...1.',
    '1.....1',
    '.1...1.',
    '..1.1..',
    '...1...',
  ],
  // △ — positive stat modifier.
  buff: [
    '...1...',
    '..111..',
    '..111..',
    '.11111.',
    '.11111.',
    '1111111',
    '1111111',
  ],
  // ▽ — negative stat modifier.
  debuff: [
    '1111111',
    '1111111',
    '.11111.',
    '.11111.',
    '..111..',
    '..111..',
    '...1...',
  ],
  // ⌛ — hourglass (timed destroy).
  timedDestroy: [
    '1111111',
    '.11111.',
    '..111..',
    '...1...',
    '..111..',
    '.11111.',
    '1111111',
  ],
  // ◎ — bullseye ("damaged you" / Root Access condition).
  damaged: [
    '.11111.',
    '1.....1',
    '1.....1',
    '1..1..1',
    '1.....1',
    '1.....1',
    '.11111.',
  ],
  // 💧 — slime-residue droplet.
  droplet: [
    '...1...',
    '..111..',
    '..111..',
    '.11111.',
    '1111111',
    '1111111',
    '.11111.',
  ],
  // ✓ — checkmark (a once-per-round ability already fired).
  check: [
    '......1',
    '.....11',
    '....11.',
    '1..11..',
    '11.1...',
    '.111...',
    '..1....',
  ],
  // ▣ — padlock (a stage that has not yet opened).
  lock: [
    '..111..',
    '.11.11.',
    '.1...1.',
    '1111111',
    '11.1.11',
    '11.1.11',
    '1111111',
  ],
  // ⚙ — cog with a hollow hub (Сборки / assemblies).
  gear: [
    '.1.1.1.',
    '1111111',
    '.11111.',
    '.11.11.',
    '.11111.',
    '1111111',
    '.1.1.1.',
  ],
  // ↯ — lightning strike through a bar (Красная зона: permanent attack boost).
  redZone: [
    '...11..',
    '..11...',
    '.111...',
    '1111111',
    '...111.',
    '..11...',
    '.11....',
  ],
  // ↩ — arrow reflecting back (Отражение use marker).
  bounce: [
    '...1...',
    '..11...',
    '.11....',
    '1111111',
    '.11....',
    '..11...',
    '...1...',
  ],
  // ⧉ — two overlapping frames (a pending self-copy).
  copy: [
    '11111..',
    '1...1..',
    '1.11111',
    '1.1.1.1',
    '1111101',
    '..1...1',
    '..11111',
  ],
  // 🗑 — bin: handle, lid, and a body ribbed by two knocked-out columns.
  trash: [
    '..111..',
    '1111111',
    '.11111.',
    '.1.1.1.',
    '.1.1.1.',
    '.1.1.1.',
    '.11111.',
  ],
  // 🌵 — thorns: a spiky burst with a hollow core (Колючки).
  thorns: [
    '...1...',
    '.1.1.1.',
    '..1.1..',
    '11...11',
    '..1.1..',
    '.1.1.1.',
    '...1...',
  ],
  // 🌱 — growth: a sprout (two leaves, stem, base) that carries the Рост count.
  growth: [
    '1.....1',
    '11...11',
    '.11.11.',
    '..111..',
    '...1...',
    '...1...',
    '.11111.',
  ],
  // 🌻 — sunflower: a solid disc ringed by petals (Подсолнух).
  sunflower: [
    '1..1..1',
    '.11111.',
    '.11111.',
    '1111111',
    '.11111.',
    '.11111.',
    '1..1..1',
  ],
  // ✿ — blossom: four round petals around a hollow core (Буйное цветение).
  bloom: [
    '.11.11.',
    '111.111',
    '.11111.',
    '..1.1..',
    '.11111.',
    '111.111',
    '.11.11.',
  ],
  // ☀ — sun: a small disc radiating eight rays (Полуденное солнце).
  sun: [
    '...1...',
    '.1...1.',
    '..111..',
    '1.111.1',
    '..111..',
    '.1...1.',
    '...1...',
  ],
  // ♜ — solid tower.
  // ▦ — a filled location: every cell of the grid taken.
  fullLocation: [
    '1111111',
    '1.1.1.1',
    '1111111',
    '1.1.1.1',
    '1111111',
    '1.1.1.1',
    '1111111',
  ],
  // ▣ — a wall block inside its frame (Barrier).
  barrier: [
    '1111111',
    '1.....1',
    '1.111.1',
    '1.111.1',
    '1.111.1',
    '1.....1',
    '1111111',
  ],
  // ◉ — a lamp's beam on its target (move already used).
  spotlight: [
    '..111..',
    '.1...1.',
    '1..1..1',
    '1.111.1',
    '1..1..1',
    '.1...1.',
    '..111..',
  ],
  // ➶ — a bolt flying up and right (support shot already used).
  support: [
    '...1111',
    '.....11',
    '....1.1',
    '...1..1',
    '..1....',
    '11.....',
    '11.....',
  ],
  // A cushion (Softening already used).
  soften: [
    '.......',
    '.11111.',
    '1111111',
    '1111111',
    '1111111',
    '.11111.',
    '.......',
  ],
  // A hunter's net.
  net: [
    '1.1.1.1',
    '.1.1.1.',
    '1.1.1.1',
    '.1.1.1.',
    '1.1.1.1',
    '.1.1.1.',
    '1.1.1.1',
  ],
  // A spiral pulled into a ball (Pull into a clot).
  clot: [
    '..111..',
    '.1...1.',
    '1..11.1',
    '1.1.1.1',
    '1.1...1',
    '.1.111.',
    '..1....',
  ],
  // T in a coin: the card is a token.
  token: [
    '.11111.',
    '1.....1',
    '1.111.1',
    '1..1..1',
    '1..1..1',
    '1.....1',
    '.11111.',
  ],
  // A stat above its base: the chip's top corner is filled.
  statUp: [
    '1111111',
    '.111111',
    '..11111',
    '...1111',
    '....111',
    '.....11',
    '......1',
  ],
  // A stat below its base: the chip's bottom corner is filled.
  statDown: [
    '......1',
    '.....11',
    '....111',
    '...1111',
    '..11111',
    '.111111',
    '1111111',
  ],
  // A crack across the art: the card is wounded.
  crack: [
    '...1...',
    '...1...',
    '..1....',
    '..11...',
    '...1...',
    '..1....',
    '.1.....',
  ],
  location0: [
    '1.1.1.1',
    '1111111',
    '.11111.',
    '.11111.',
    '.11111.',
    '.11111.',
    '1111111',
  ],
  // ▲ — peak.
  location1: [
    '...1...',
    '..111..',
    '.11111.',
    '1111111',
    '1111111',
    '1111111',
    '1111111',
  ],
  // ♖ — hollow tower.
  location2: [
    '1.1.1.1',
    '1111111',
    '1.....1',
    '1.....1',
    '1.....1',
    '1.....1',
    '1111111',
  ],
  // ⌂ — house: solid roof over a body with the doorway punched out (Главная).
  home: [
    '....1....',
    '...111...',
    '..11111..',
    '.1111111.',
    '111111111',
    '.1111111.',
    '.11...11.',
    '.11...11.',
    '.11...11.',
  ],
  // 🂠 — two solid cards staggered, split by an empty column (Колоды).
  decks: [
    '.....111.',
    '.....1111',
    '.....1111',
    '.111.1111',
    '1111.1111',
    '1111.111.',
    '1111.....',
    '1111.....',
    '.111.....',
  ],
  // ▤ — four tiles around a cross-shaped gutter (Каталог).
  catalog: [
    '1111.1111',
    '1111.1111',
    '1111.1111',
    '1111.1111',
    '.........',
    '1111.1111',
    '1111.1111',
    '1111.1111',
    '1111.1111',
  ],
  // 🏆 — trophy: cup with side handles on a plinth (Челленджи).
  challenges: [
    '.1111111.',
    '111111111',
    '1.11111.1',
    '..11111..',
    '...111...',
    '....1....',
    '...111...',
    '..11111..',
    '.1111111.',
  ],
  // 🧪 — Erlenmeyer flask, filled to the shoulder (Песочница / lab).
  sandbox: [
    '..11111..',
    '...1.1...',
    '...1.1...',
    '...1.1...',
    '..11.11..',
    '..1...1..',
    '.1111111.',
    '111111111',
    '.1111111.',
  ],
  // 💬 — solid speech bubble with three knocked-out dots and a tail (Фидбек).
  feedback: [
    '.1111111.',
    '111111111',
    '111111111',
    '11.1.1.11',
    '111111111',
    '.1111111.',
    '.111.....',
    '.11......',
    '.1.......',
  ],
  // 🔔 — bell over its clapper (Обновления).
  changelog: [
    '....1....',
    '...111...',
    '..11111..',
    '..11111..',
    '.1111111.',
    '.1111111.',
    '111111111',
    '.........',
    '...111...',
  ],
  // ⇥ — arrow leaving a door frame (Выйти).
  logout: [
    '11111....',
    '11..1....',
    '11..1.1..',
    '11..1..1.',
    '11..11111',
    '11..1..1.',
    '11..1.1..',
    '11..1....',
    '11111....',
  ],
  // Ж — heavy cyrillic letter for the bold toolbar button.
  bold: [
    '1..1..1',
    '11.1.11',
    '.11111.',
    '..111..',
    '.11111.',
    '11.1.11',
    '1..1..1',
  ],
  // К — slanted letter for the italic toolbar button.
  italic: [
    '..1..11',
    '..1.11.',
    '.1.11..',
    '.111...',
    '.1.11..',
    '1...11.',
    '1....11',
  ],
  // ☰ with bullets — the bulleted-list toolbar button.
  bulletList: [
    '1.11111',
    '.......',
    '1.11111',
    '.......',
    '1.11111',
    '.......',
    '1.111..',
  ],
  // 📋 — clipboard: paste a deck code.
  paste: [
    '..111..',
    '1111111',
    '1.....1',
    '1.111.1',
    '1.....1',
    '1.111.1',
    '1111111',
  ],
  // 🗃 — box with a lid: archive.
  archive: [
    '1111111',
    '1111111',
    '.1...1.',
    '.1.1.1.',
    '.1...1.',
    '.1...1.',
    '.11111.',
  ],
  // + and − for the deck catalog steppers: Press Start 2P has no U+2212 glyph.
  plus: [
    '.......',
    '...1...',
    '...1...',
    '.11111.',
    '...1...',
    '...1...',
    '.......',
  ],
  minus: [
    '.......',
    '.......',
    '.......',
    '.11111.',
    '.......',
    '.......',
    '.......',
  ],
  // ⌕ — magnifier for search fields.
  search: [
    '.111...',
    '1...1..',
    '1...1..',
    '1...1..',
    '.111...',
    '....11.',
    '.....11',
  ],
  // ⌄ — the select arrow.
  chevronDown: [
    '.......',
    '.......',
    '1.....1',
    '11...11',
    '.11.11.',
    '..111..',
    '...1...',
  ],
};

/** Every icon name, so tests and docs can enumerate the whole set. */
export const PIXEL_ICON_NAMES = Object.keys(ICONS) as readonly PixelIconName[];

/**
 * The icon as a CSS `url()` data URI in a fixed colour, for the few places a
 * native control cannot host an <svg> child (the <select> arrow). The colour
 * must come from `COLORS`, so the token stays the single source.
 */
export function pixelIconDataUri(name: PixelIconName, color: string): string {
  const rows = ICONS[name];
  const width = rows[0]?.length ?? 0;
  const cells = rows.flatMap((row, y) => [...row].flatMap((pixel, x) =>
    pixel === '.' ? [] : [`M${x} ${y}h1v1h-1z`]));
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${rows.length}' shape-rendering='crispEdges'><path fill='${color}' d='${cells.join('')}'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

export interface PixelIconProps {
  name: PixelIconName;
  /** Explicit px size; omit to let CSS size the SVG to 1em (matches text glyphs). */
  size?: number;
  className?: string;
}

function PixelIconComponent({ name, size, className }: PixelIconProps): ReactElement {
  const rows = ICONS[name];
  const width = rows[0]?.length ?? 0;
  const height = rows.length;
  return (
    <svg
      data-testid={`pixel-icon-${name}`}
      data-icon={name}
      viewBox={`0 0 ${width} ${height}`}
      {...(size === undefined ? {} : { width: size, height: size })}
      aria-hidden="true"
      focusable="false"
      shapeRendering="crispEdges"
      className={className === undefined ? 'cards-pixel-icon' : `cards-pixel-icon ${className}`}
    >
      {rows.flatMap((row, y) => [...row].map((pixel, x) => (
        pixel === '.' ? null : (
          <rect key={`${x}:${y}`} x={x} y={y} width="1" height="1" fill="currentColor" />
        )
      )))}
    </svg>
  );
}

/**
 * Memoized on its three scalar props. An icon is up to ~80 <rect> nodes and the
 * board wears dozens of them (stat glyphs, keyword badges, status chips), all
 * re-created on every playback step by parents whose own props did change. The
 * art for a given name never changes, so the whole subtree can be skipped.
 */
export const PixelIcon = memo(PixelIconComponent);

/**
 * Known authored text/emoji symbols with a pixel-art equivalent. Authored UI
 * metadata stays a plain string (redaction-safe, card-agnostic); the web layer
 * upgrades recognized symbols to pixel art so no raster emoji leaks into the
 * pixel aesthetic.
 */
const SYMBOL_ICONS: Readonly<Record<string, PixelIconName>> = {
  '💧': 'droplet',
  '🌵': 'thorns',
  '🌱': 'growth',
  '🌻': 'sunflower',
  '⚔': 'attack',
  '✿': 'bloom',
  '☀': 'sun',
  '✓': 'check',
  '⚙': 'gear',
  '↯': 'redZone',
  '↩': 'bounce',
  '⧉': 'copy',
  '▦': 'fullLocation',
  '▣': 'barrier',
  '◉': 'spotlight',
  '➶': 'support',
  '−2': 'soften',
  '⌗': 'net',
  '◍': 'clot',
};

export function pixelIconForSymbol(symbol: string): PixelIconName | undefined {
  return SYMBOL_ICONS[symbol];
}

/**
 * Renders an authored symbol as pixel art when an equivalent icon exists.
 * Authored status symbols often bundle a counter after the emoji (e.g. `🌵3`,
 * `🌱5`); we upgrade the leading emoji to pixel art and keep the trailing text
 * so no raster emoji leaks into the pixel aesthetic.
 */
export function PixelSymbol({ symbol, className }: {
  symbol: string;
  className?: string;
}): ReactElement {
  const exact = pixelIconForSymbol(symbol);
  if (exact !== undefined) {
    return <PixelIcon name={exact} {...(className === undefined ? {} : { className })} />;
  }
  const chars = [...symbol];
  const lead = chars[0];
  const leadIcon = lead === undefined ? undefined : pixelIconForSymbol(lead);
  if (leadIcon !== undefined) {
    return (
      <>
        <PixelIcon name={leadIcon} {...(className === undefined ? {} : { className })} />
        {chars.slice(1).join('')}
      </>
    );
  }
  return <>{symbol}</>;
}
